import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { createPortal } from 'react-dom';
import '../../styles/inputs/settingsIconSelectInput.scss';
import CustomSVGs from '../customSVGs/CustomSVGs';
import { arrowHeadRightLightSVG, tickSVG } from '../../lib/allCustomSVGs';

type ItemProps = {
  icon?: any;
  imageIcon?: string; // URL for image icon
  text: string;
  value: string;
};

interface Props {
  filterOptions: ItemProps[];
  onFilterChange?: (selectedValue: string) => void;
  value: string;
  title?: string;
  marginTop?: string;
  hideTick?: boolean;
  scrollWhenOpen?: boolean; // ✅ Optional control prop
  readonly?: boolean; // ✅ Optional prop to make input read-only
  emptyOptionPlaceholder?: string; // Placeholder text when no option is selected
  loading?: boolean; // ✅ Optional prop to show loading spinner instead of arrow
  onMenuClose?: () => void; // ✅ Optional callback when menu should be closed
}

export interface SettingsIconSelectInputRef {
  closeMenu: () => void;
}

type MenuPos = {
  top: number;
  left: number;
  width: number;
  placement: 'bottom' | 'top';
};

const GAP = 8; // space between trigger and menu

const SettingsIconSelectInput = forwardRef<SettingsIconSelectInputRef, Props>(
  (
    {
      filterOptions,
      onFilterChange,
      value,
      title,
      hideTick,
      marginTop = '0',
      scrollWhenOpen = false,
      readonly = false,
      emptyOptionPlaceholder = '-- Select --',
      loading = false,
      onMenuClose,
    },
    ref
  ) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [menuPos, setMenuPos] = useState<MenuPos | null>(null);

    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);

    const matchedOption = filterOptions.find(
      (option) => option.value === value
    );
    const selectedFilter = matchedOption || {
      text: emptyOptionPlaceholder,
      value: '',
      icon: null,
      imageIcon: undefined,
    };

    /** Compute initial position based on trigger rect (no height yet). */
    const computeBasePosition = (): MenuPos | null => {
      if (!triggerRef.current) return null;
      const rect = triggerRef.current.getBoundingClientRect();
      const width = Math.max(rect.width, 180);
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const heightGuess = 200;
      const placement: 'bottom' | 'top' =
        spaceBelow >= heightGuess || spaceBelow >= spaceAbove
          ? 'bottom'
          : 'top';

      // ✅ Align right edge of menu to right edge of trigger
      const left = rect.right + window.scrollX - width;

      const baseTop =
        placement === 'bottom'
          ? rect.bottom + window.scrollY + GAP
          : rect.top + window.scrollY - GAP;
      return { top: baseTop, left, width, placement };
    };

    /** Refine top using actual menu height (flip up if needed). */
    const refineWithMeasuredHeight = () => {
      if (!menuRef.current || !triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      let placement: 'bottom' | 'top' =
        spaceBelow >= menuRect.height || spaceBelow >= spaceAbove
          ? 'bottom'
          : 'top';

      // ✅ Align right
      const left = rect.right + window.scrollX - menuRect.width;

      const top =
        placement === 'bottom'
          ? rect.bottom + window.scrollY + GAP
          : rect.top + window.scrollY - menuRect.height - GAP;

      setMenuPos((prev) =>
        prev
          ? { ...prev, top: Math.max(8, top), left, placement }
          : {
              top: Math.max(8, top),
              left,
              width: Math.max(rect.width, 180),
              placement,
            }
      );
    };

    /** Open/close + positioning */
    const openMenu = () => {
      setDropdownOpen(true);
      const base = computeBasePosition();
      setMenuPos(base);
      if (scrollWhenOpen && rootRef.current) {
        rootRef.current.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    };
    const closeMenu = () => {
      setDropdownOpen(false);
      onMenuClose?.();
    };

    useImperativeHandle(ref, () => ({
      closeMenu,
    }));

    /** Update position on scroll/resize while open */
    useLayoutEffect(() => {
      if (!dropdownOpen) return;
      const onScrollOrResize = () => {
        const base = computeBasePosition();
        setMenuPos(base);
        // refine after next paint so menu can measure
        requestAnimationFrame(refineWithMeasuredHeight);
      };
      onScrollOrResize();
      window.addEventListener('scroll', onScrollOrResize, true);
      window.addEventListener('resize', onScrollOrResize);
      return () => {
        window.removeEventListener('scroll', onScrollOrResize, true);
        window.removeEventListener('resize', onScrollOrResize);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdownOpen]);

    /** After menu renders, measure real height and refine placement */
    useLayoutEffect(() => {
      if (!dropdownOpen) return;
      // give the portal a tick to mount
      requestAnimationFrame(refineWithMeasuredHeight);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdownOpen, filterOptions.length]);

    /** Close on outside click (must consider portal content) */
    useEffect(() => {
      if (!dropdownOpen || loading) return; // Don't close if loading
      const onDown = (e: MouseEvent) => {
        const t = e.target as Node;
        const insideTrigger =
          !!triggerRef.current && triggerRef.current.contains(t);
        const insideMenu = !!menuRef.current && menuRef.current.contains(t);
        if (!insideTrigger && !insideMenu) closeMenu();
      };
      document.addEventListener('mousedown', onDown);
      return () => document.removeEventListener('mousedown', onDown);
    }, [dropdownOpen, loading]);

    /** Keyboard support: Esc closes, Enter toggles */
    useEffect(() => {
      if (!dropdownOpen || loading) return; // Don't close if loading
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          closeMenu();
        }
      };
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }, [dropdownOpen, loading]);

    return (
      <>
        <style>
          {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
        </style>
        <div
          ref={rootRef}
          style={{ marginTop, position: 'relative' }}
          className='settingsIconSelectInput'
        >
          {title && (
            <div className='settingsIconSelectInput-title'>{title}</div>
          )}

          {/* Trigger / Display */}
          <div
            ref={triggerRef}
            className='settingsIconSelectInput__filter'
            onClick={() => {
              if (readonly || loading) return; // Prevent interaction if read-only or loading

              dropdownOpen ? closeMenu() : openMenu();
            }}
            data-is-open={dropdownOpen ? 'true' : 'false'}
            role='button'
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (readonly || loading) return; // Prevent interaction if read-only or loading
                dropdownOpen ? closeMenu() : openMenu();
              }
            }}
          >
            <div className='settingsIconSelectInput__filter--left'>
              {selectedFilter.icon && (
                <CustomSVGs
                  className='settingsIconSelectInput__filter--icon'
                  svg={selectedFilter.icon}
                />
              )}
              {selectedFilter.imageIcon && (
                <img
                  className='settingsIconSelectInput__filter--img'
                  src={selectedFilter.imageIcon}
                  alt={selectedFilter.text}
                />
              )}
              <div className='settingsIconSelectInput__filter--text'>
                {selectedFilter.text}
              </div>
            </div>

            {loading ? (
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #f3f3f3',
                  borderTop: '2px solid #3498db',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
            ) : (
              <CustomSVGs
                className='settingsIconSelectInput__filter--arrow'
                svg={arrowHeadRightLightSVG}
              />
            )}
          </div>

          {/* Keep old container in DOM for compatibility, but unused now */}
          <div
            className='settingsIconSelectInput__dropdown--container'
            style={{ display: 'none' }}
          />

          {/* Portal menu */}
          {dropdownOpen &&
            menuPos &&
            createPortal(
              <div
                ref={(el) => (menuRef.current = el)}
                className='settingsIconSelectInput__dropdown settingsIconSelectInput__dropdown--elevated'
                style={{
                  position: 'absolute',
                  top: menuPos.top,
                  left: menuPos.left,
                  minWidth: menuPos.width,
                  maxWidth: 'calc(100vw - 16px)',
                  zIndex: 10000,
                }}
                role='listbox'
                aria-label={title || 'Select option'}
              >
                {filterOptions.map((option) => {
                  const isSelected = value === option.value;
                  return (
                    <div
                      key={option.value}
                      className={`settingsIconSelectInput__dropdown-item ${
                        isSelected ? 'selected' : ''
                      }`}
                      role='option'
                      aria-selected={isSelected}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (option.value !== value) {
                          onFilterChange?.(option.value);
                          // Don't close menu - let the parent component handle it after API call
                          // The parent will close it when the API call completes
                          closeMenu(); // ✅ close immediately after selection
                        } else {
                          // Only close if clicking the same option and not loading
                          if (!loading) {
                            closeMenu();
                          }
                        }
                      }}
                    >
                      <div
                        style={{
                          opacity: isSelected ? '1' : '0',
                          display: hideTick ? 'none' : 'block',
                        }}
                        className='settingsIconSelectInput__filter--tick'
                        aria-hidden
                      >
                        <CustomSVGs svg={tickSVG} />
                      </div>
                      {option.icon && <CustomSVGs svg={option.icon} />}
                      {option?.imageIcon && (
                        <img src={option.imageIcon} alt={option.text} />
                      )}
                      <span>{option.text}</span>
                    </div>
                  );
                })}
              </div>,
              document.body
            )}
        </div>
      </>
    );
  }
);

export default SettingsIconSelectInput;
